import React, { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Language, useLanguage } from "../../../i18n/useLanguage";
import {
  PackageItem,
  PackagePartItem,
} from "../../../queries/useSearchPartPackagesByVehicle";
import { PackageEditorQuantityRecord } from "../../../stores/slices/packageEditorSlice";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { useForm } from "../../../util/useForm";
import { PriceSelect } from "../PriceSelect/PriceSelect";

const schema = z.record(z.string());

export const packageEditorPartsFormId = "package-editor-parts-form";

export const PackageParts: FunctionComponent<{
  getPartQuantity(part: PackageItem): string;
  selectedPackage?: PackagePartItem;
  setQuantity(quantity: PackageEditorQuantityRecord): void;
}> = ({ getPartQuantity, selectedPackage, setQuantity }) => {
  const [language] = useLanguage();
  const formRef = useRef<HTMLFormElement>(null);

  const [handleForm] = useForm({
    onSubmit(form) {
      setQuantity(form);
    },
    schema,
  });

  return (
    <form
      className="grid gap-2 px-4"
      id={packageEditorPartsFormId}
      noValidate
      onChange={handleForm}
      ref={formRef}
    >
      {selectedPackage?.partDetails.primaryPart.packageItem.map((item) => {
        return (
          <Row
            getPartQuantity={getPartQuantity}
            item={item}
            key={item.itemId || item.itemPartNumber}
            language={language}
          />
        );
      })}
    </form>
  );
};

const Row: FunctionComponent<{
  getPartQuantity(part: PackageItem): string;
  item: PackageItem;
  language: Language;
}> = ({ getPartQuantity, item, language }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2" key={item.itemId}>
      <div>
        <div>{item.itemTitle[language] || item.itemTitle["en"]}</div>
        <div className="text-xs inline-flex gap-2">
          <KeyValue>
            <>{t("packageEditor.partType")}:</>
            {item.itemTypeDescription[language] ||
              item.itemTypeDescription["en"]}
          </KeyValue>
          <KeyValue>
            <>{t("packageEditor.partNumber")}:</>
            {item.itemPartNumber}
          </KeyValue>
        </div>
      </div>
      <PriceSelect defaultValue={getPartQuantity(item)} name={item.itemId} />
    </div>
  );
};
